import { useCallback } from 'react';
import { Control, FieldValues, FieldPath } from 'react-hook-form';
import AsyncSearchSelectRHF, {
  AsyncSearchSelectProps,
} from '@/components/asyncSelect/AsyncSearchSelectRHF.tsx';
import bookingsApi from '@/features/dashboard/bookings/api/bookingsApi.ts';
import { bookingsApiQueryKeys } from '@/features/dashboard/bookings/api/bookingsApi.queryKeys.ts';
import { BookingListDto } from '@/features/dashboard/bookings/types/BookingListDto.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { FilterFieldOperator } from '@/components/table/_filterFieldOperators/FilterFieldOperator.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import { bookingOption } from '@/components/asyncSelect/entitySelects/entityOptions.tsx';
import { withCancelledShown } from '@/components/asyncSelect/asyncSelectFilters.ts';

type BookingSearchSelectProps<T extends FieldValues> = Omit<
  AsyncSearchSelectProps<T, BookingListDto>,
  'fetchFn' | 'toOption' | 'queryKey'
> & {
  fieldPathName: FieldPath<T>;
  control: Control<T>;
  clientId: string;
};

const BookingSearchSelectRHF = <T extends FieldValues>({
  clientId,
  ...props
}: BookingSearchSelectProps<T>) => {
  // Pusty `clientId` nie może trafić do filtra ID — backend odrzuca go jako
  // `NotBlank`; bez klienta select i tak nic nie pobiera (`enabled`).
  const fetchFn = useCallback(
    (pageRequest: PageRequest, filter: FilterCriteriaDto) => {
      const filterWithClient: FilterCriteriaDto = clientId
        ? {
            ...filter,
            filters: [
              ...filter.filters,
              {
                fieldId: 'clientId',
                type: FieldType.ID,
                operator: FilterFieldOperator.EQUALS,
                value: clientId,
              },
            ],
          }
        : filter;
      return bookingsApi.getAll(
        pageRequest,
        withCancelledShown(filterWithClient),
      );
    },
    [clientId],
  );

  return (
    <AsyncSearchSelectRHF<T, BookingListDto>
      {...props}
      queryKey={[...bookingsApiQueryKeys.all(), 'byClient', clientId]}
      fetchFn={fetchFn}
      toOption={bookingOption}
      enabled={Boolean(clientId)}
      // Wybór klienta czyści cache i doczytuje pierwszą stronę rezerwacji, więc
      // lista pojawia się bez wpisywania czegokolwiek.
      cacheUniqs={[clientId]}
      isDisabled={!clientId}
    />
  );
};

export default BookingSearchSelectRHF;
